import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {IPermission} from './permission';
import {distinctUntilChanged, filter, map, take, tap} from 'rxjs/operators';
import {isEqual, isNil} from 'lodash-es';
import {MIHttpClientService} from '../shared/mi-http-client.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {

  private permissionsCache: { [organizationId: number]: IPermission[] } =
    JSON.parse(localStorage.getItem('permissionsCache')) || {};

  private permissionsSubject: Subject<IPermission[]> = new BehaviorSubject<IPermission[]>(null);
  private permissions$: Observable<IPermission[]> = this.permissionsSubject.asObservable()
    .pipe(
      filter(permissions => !isNil(permissions)),
      distinctUntilChanged(isEqual),
    );

  constructor(
    private miClient: MIHttpClientService,
  ) {
  }

  private fetchPermissions(organizationId: number): Observable<IPermission[]> {
    return this.miClient.get<IPermission[]>('permissions')
      .pipe(
        tap((permissions: IPermission[]) => this.permissionsCache[organizationId] = permissions),
      );
  }

  public getPermissions(organizationId: number = +localStorage.getItem('selectedOrganization')): Observable<IPermission[]> {
    let permissions: Observable<IPermission[]>;
    if (this.permissionsCache[organizationId]) {
      permissions = of<IPermission[]>(this.permissionsCache[organizationId]);
    } else {
      permissions = this.fetchPermissions(organizationId);
    }
    return permissions.pipe(
      tap((fetched: IPermission[]) => this.permissionsSubject.next(fetched)),
      tap(() => localStorage.setItem('permissionsCache', JSON.stringify(this.permissionsCache))),
    );
  }

  public hasPermission(name: string): Observable<boolean> {
    if (isNil(name)) {
      return of(false);
    }
    return this.permissions$.pipe(
      map(permissions => !!permissions.find(({ permissionRight }) => permissionRight === name)),
    );
  }

  public executeIfPermission(permission: string, executeBlock: Function, context?: any) {
    this.hasPermission(permission).pipe(take(1)).subscribe(
        hasPermission => {
          if (hasPermission) {
            if (context !== null) {
              executeBlock.call(context);
            } else {
              executeBlock.call(this);
            }
          }
        },
    );
  }


  public hasAtLeastOnePermission(...args: string[]): Observable<boolean> {
    return this.permissions$.pipe(
      map(permissions => permissions.filter(({ permissionRight }) => args.includes(permissionRight)).length > 0),
    );
  }

  public hasMandatoryAndAtLeastOnePermission(mandatoryPermission: string, ...args: string[]): Observable<boolean> {
    return this.permissions$.pipe(
      map(permissions =>
        !!permissions.find(({ permissionRight }) => permissionRight === mandatoryPermission) &&
        permissions.filter(({ permissionRight }) => args.includes(permissionRight)).length > 0),
    );
  }



  public clearCache(): void {
    this.permissionsCache = {};
    localStorage.removeItem('permissionsCache');
    this.permissionsSubject.next(null);
  }

  getAllPermissions(): Observable<IPermission[]> {
    return this.miClient.get<any>('allPermissions').pipe(
      map(permissions => permissions.sort((a, b) => (a.permissionRight > b.permissionRight) ? 1 : -1)),
    );
  }

  updatePermissionsBulk(permissions: any): Observable<any> {
    return this.miClient.post('permissionsInBulk', permissions);
  }

  canUpdatePermissions(): Observable<any> {
    if(localStorage.getItem('selectedOrganization') === null) {
      return of(false);
    }
    return this.miClient.get('canUpdatePermissions');
  }


}
